import {
  AfterViewInit,
  Component,
  computed,
  inject,
  OnDestroy,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { BreadcrumbComponent } from '../breadcrumb/breadcrumb.component';
import { TopbarComponent } from '../topbar/topbar.component';
import { SidebarComponent } from '../sidebar/sidebar.component';

import { ToastModule } from 'primeng/toast';
import { SaveableComponent } from 'src/app/shared/interfaces/saveable-component';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { SearchComponent } from '../search/search.component';
import { LayoutService } from 'src/app/shared/services/layout.service';
import { SaveableTrackerService } from 'src/app/shared/services/saveable-tracker.service';
import { LayoutConfiguratorComponent } from '../layout-configurator/layout-configurator.component';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    CommonModule,
    ToastModule,
    RouterModule,
    BreadcrumbComponent,
    SearchComponent,
    ConfirmDialogModule,
    ConfirmPopupModule,
    LayoutConfiguratorComponent,
    SidebarComponent,
  ],
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.css',
})
export class LayoutComponent implements AfterViewInit, OnDestroy {
  overlayMenuOpenSubscription: Subscription;

  private outsideHandler!: (ev: MouseEvent) => void;

  menuScrollListener: any;

  @ViewChild(SidebarComponent) appSidebar!: SidebarComponent;

  @ViewChild(TopbarComponent) appTopBar!: TopbarComponent;

  saveableTracker = inject(SaveableTrackerService);

  constructor(
    public layoutService: LayoutService,
    public renderer: Renderer2,
    public router: Router,
  ) {
    this.overlayMenuOpenSubscription =
      this.layoutService.overlayOpen$.subscribe(() => {
        if (
          (this.layoutService.isHorizontal() ||
            this.layoutService.isSlim() ||
            this.layoutService.isCompact()) &&
          !this.menuScrollListener
        ) {
          this.menuScrollListener = this.renderer.listen(
            this.appSidebar.menuContainer.nativeElement,
            'scroll',
            (event) => {
              if (this.layoutService.isDesktop()) {
                this.hideMenu();
              }
            },
          );
        }
        if (this.layoutService.layoutState().staticMenuMobileActive) {
          this.blockBodyScroll();
        }
      });

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.hideMenu();

        const container = document.querySelector('.layout-content-wrapper');
        if (container) {
          (container as HTMLElement).scrollTop = 0;
        }
      });
  }

  ngAfterViewInit() {
    this.outsideHandler = (event: MouseEvent) =>
      this.outsideClickListener(event);
    document.addEventListener('click', this.outsideHandler, true);
  }

  onComponentActivate(componentRef: any) {
    if (typeof componentRef.save === 'function' && componentRef.isDirty) {
      // Komponente hat eine save()-Methode => ist "SaveableComponent"
      this.saveableTracker.set(componentRef as SaveableComponent);
    } else {
      this.saveableTracker.set(null);
    }
  }
  outsideClickListener(event: MouseEvent) {
    if (this.isOutsideClicked(event)) {
      this.hideMenu();
    }
  }

  isOutsideClicked(event: MouseEvent) {
    const target = event.target as HTMLElement;

    const sidebarEl = document.querySelector('.layout-sidebar');
    const topbarButtonEl = document.querySelector('.topbar-left > a');
    const overlaySubmenuEl = document.querySelector('.layout-root-submenulist'); // falls vorhanden

    return !(
      sidebarEl?.contains(target) ||
      topbarButtonEl?.contains(target) ||
      overlaySubmenuEl?.contains(target)
    );
  }

  hideMenu() {

    this.layoutService.layoutState.update((prev) => ({
      ...prev,
      overlayMenuActive: false,
      staticMenuMobileActive: false,
      overlaySubmenuActive: false,
      menuHoverActive: false,
    }));

    if (
      this.layoutService.isCompact() ||
      this.layoutService.isSlim() ||
      this.layoutService.isHorizontal()
    ) {
      this.layoutService.reset();
    }

    if (this.layoutService.isHorizontal()) {
      this.layoutService.onMenuStateChange({ key: '' });
    }

    if (this.menuScrollListener) {
      this.menuScrollListener();
      this.menuScrollListener = null;
    }

    this.unblockBodyScroll();
  }

  blockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.add('blocked-scroll');
    } else {
      document.body.className += ' blocked-scroll';
    }
  }

  unblockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.remove('blocked-scroll');
    } else {
      document.body.className = document.body.className.replace(
        new RegExp(
          '(^|\\b)' + 'blocked-scroll'.split(' ').join('|') + '(\\b|$)',
          'gi',
        ),
        ' ',
      );
    }
  }

  containerClass = computed(() => {
    const layoutConfig = this.layoutService.layoutConfig();
    const layoutState = this.layoutService.layoutState();

    return {
      'layout-overlay': layoutConfig.menuMode === 'overlay',
      'layout-static': layoutConfig.menuMode === 'static',
      'layout-slim': layoutConfig.menuMode === 'slim',
      'layout-horizontal': layoutConfig.menuMode === 'horizontal',
      'layout-compact': layoutConfig.menuMode === 'compact',
      'layout-reveal': layoutConfig.menuMode === 'reveal',
      'layout-drawer': layoutConfig.menuMode === 'drawer',
      'layout-overlay-active':
        layoutState.overlayMenuActive || layoutState.staticMenuMobileActive,
      'layout-mobile-active': layoutState.staticMenuMobileActive,
      'layout-static-inactive':
        layoutState.staticMenuDesktopInactive &&
        layoutConfig.menuMode === 'static',
      'layout-sidebar-active': layoutState.sidebarActive,
      'layout-sidebar-anchored': layoutState.anchored,
    };
  });

  ngOnDestroy() {
    if (this.overlayMenuOpenSubscription) {
      this.overlayMenuOpenSubscription.unsubscribe();
    }

    document.removeEventListener('click', this.outsideHandler, true);
  }
}
